import { LightningElement, api, wire } from "lwc";
import { default as cx } from "classnames";
import { logger } from "@devforce/trailhead-client-logger";

import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { assignedSlotNames } from "common/slot";
import { BooleanAttr, attrToBool } from "common/helpers";

import * as locales from "./locales";

type ModalSize = "x-small" | "small" | "medium" | "large";

export default class extends LightningElement {
  @api assistiveText: string | null = null;
  @api disableClose: BooleanAttr = null;
  @api dismissOnClickOutside: BooleanAttr = "";
  @api fullHeight: BooleanAttr = null;
  @api open: BooleanAttr = null;
  @api size: ModalSize | null = null;

  private slots: string = "";

  private initialOverflowValue: string = "";

  @wire(LabelsAdapter, { locales })
  private labels: LabelsAdapterValue = null!;

  connectedCallback() {
    if (!this._disableClose) {
      window.addEventListener("keydown", this.handleKeyDown);
    }
  }

  disconnectedCallback() {
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  private get isOpen() {
    return attrToBool(this.open);
  }

  private get _disableClose() {
    return attrToBool(this.disableClose);
  }

  private get hasNoHeading() {
    return !this.slots.includes("heading");
  }

  private get dialogLabelledBy() {
    return this.assistiveText ? null : "modal-heading";
  }

  private get className() {
    return cx("modal", {
      [`modal--${this.size}`]: this.size,
      [`modal--full-height`]: attrToBool(this.fullHeight),
    });
  }

  private get dialogAriaLabel() {
    return this.assistiveText || null;
  }

  private handleSlotChange() {
    if (!this.isOpen) {
      return;
    }
    this.slots = assignedSlotNames(this.template).join(" ");

    if (this.hasNoHeading && !this.assistiveText) {
      logger.warn("A modal must include either a heading or assistive-text.");
    }
  }

  private handleRequestClose = () => {
    this.dispatchEvent(new CustomEvent("requestclose"));
  };

  // Close when user presses escape key
  private handleKeyDown = (e: KeyboardEventInit) => {
    if (e.key === "Esc" || e.key === "Escape") {
      this.handleRequestClose();
    }
  };

  // Close when user clicks outside (on backdrop)
  private handleOutsideClick(e: MouseEvent) {
    if (!attrToBool(this.dismissOnClickOutside) || this._disableClose) {
      return;
    }

    const container = this.template.querySelector(".modal__container");

    if ((e.target as HTMLElement)!.contains(container)) {
      this.handleRequestClose();
    }
  }
}
